import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { CustomFieldInputs } from "@/components/CustomFieldInputs";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCustomFieldDefinitions } from "@/hooks/useCustomFieldDefinitions";
import { usePartsAndLocations } from "@/hooks/usePartsAndLocations";
import type { CustomFieldDefinition } from "@/types/customFields";
import { apiFetch } from "@/utils/api";

interface PartRow {
  id: string;
  part_name: string;
  part_number: string;
  description: string;
  default_location_id: string | null;
  location_name: string;
  minimum_stock: number;
  on_hand: number;
  customFields?: Record<string, string>;
}

const emptyForm = {
  partName: "",
  partNumber: "",
  description: "",
  defaultLocationId: "",
};

function initCustomFieldValues(
  definitions: CustomFieldDefinition[],
  existing?: Record<string, string>,
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const def of definitions) {
    values[def.fieldKey] =
      existing?.[def.fieldKey] ?? (def.fieldType === "boolean" ? "false" : "");
  }
  return values;
}

interface PartFormFieldsProps {
  idPrefix: string;
  form: typeof emptyForm;
  onFormChange: (next: typeof emptyForm) => void;
  locations: { id: string; name: string }[];
  customFields: CustomFieldDefinition[];
  customFieldValues: Record<string, string>;
  onCustomFieldChange: (fieldKey: string, value: string) => void;
}

function PartFormFields({
  idPrefix,
  form,
  onFormChange,
  locations,
  customFields,
  customFieldValues,
  onCustomFieldChange,
}: PartFormFieldsProps) {
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-part-name`}>Part name *</Label>
        <Input
          id={`${idPrefix}-part-name`}
          value={form.partName}
          onChange={(e) => onFormChange({ ...form, partName: e.target.value })}
          required
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-part-number`}>Part number</Label>
        <Input
          id={`${idPrefix}-part-number`}
          value={form.partNumber}
          onChange={(e) => onFormChange({ ...form, partNumber: e.target.value })}
        />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor={`${idPrefix}-part-description`}>Description</Label>
        <Input
          id={`${idPrefix}-part-description`}
          value={form.description}
          onChange={(e) => onFormChange({ ...form, description: e.target.value })}
        />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor={`${idPrefix}-part-location`}>Default location</Label>
        <Select
          id={`${idPrefix}-part-location`}
          value={form.defaultLocationId}
          onChange={(e) => onFormChange({ ...form, defaultLocationId: e.target.value })}
        >
          <option value="">None</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </Select>
        {locations.length === 0 && (
          <p className="text-xs text-muted-foreground">
            <Link to="/admin/locations" className="underline text-[#1e3a4f]">
              Add locations
            </Link>{" "}
            before assigning a default.
          </p>
        )}
      </div>
      <CustomFieldInputs
        definitions={customFields}
        values={customFieldValues}
        onChange={onCustomFieldChange}
        idPrefix={idPrefix}
      />
    </>
  );
}

export function AdminPartsPage() {
  const [parts, setParts] = useState<PartRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const { locations, loading: locLoading } = usePartsAndLocations();
  const { definitions: customFields, loading: fieldsLoading } = useCustomFieldDefinitions("part");

  const load = async () => {
    const data = await apiFetch<{ parts: PartRow[] }>("/parts");
    setParts(data.parts);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const startEdit = (part: PartRow) => {
    setShowNew(false);
    setEditingId(part.id);
    setFormError("");
    setForm({
      partName: part.part_name,
      partNumber: part.part_number ?? "",
      description: part.description ?? "",
      defaultLocationId: part.default_location_id ?? "",
    });
    setCustomFieldValues(initCustomFieldValues(customFields, part.customFields));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setCustomFieldValues(initCustomFieldValues(customFields));
    setFormError("");
  };

  const resetCreateForm = () => {
    setForm(emptyForm);
    setCustomFieldValues(initCustomFieldValues(customFields));
    setFormError("");
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      await apiFetch("/parts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          partName: form.partName,
          partNumber: form.partNumber,
          description: form.description,
          defaultLocationId: form.defaultLocationId || undefined,
          customFields: customFieldValues,
        }),
      });
      setShowNew(false);
      resetCreateForm();
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create part");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    setFormError("");
    setSaving(true);
    try {
      await apiFetch(`/parts/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          partName: form.partName,
          partNumber: form.partNumber,
          description: form.description,
          defaultLocationId: form.defaultLocationId || null,
          customFields: customFieldValues,
        }),
      });
      cancelEdit();
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to update part");
    } finally {
      setSaving(false);
    }
  };

  if (loading || locLoading || fieldsLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Parts"
        subtitle={
          <>
            <Link to="/admin/custom-fields?type=part" className="underline text-[#1e3a4f]">
              Manage custom fields
            </Link>
          </>
        }
        actions={
          <Button
            type="button"
            onClick={() => {
              if (showNew) {
                setShowNew(false);
                resetCreateForm();
              } else {
                setShowNew(true);
                setEditingId(null);
                resetCreateForm();
              }
            }}
            className="bg-[#1e3a4f] hover:bg-[#163041]"
          >
            {showNew ? "Cancel" : "Add Part"}
          </Button>
        }
      />

      {showNew && (
        <form
          className="bg-white rounded-lg shadow p-4 grid gap-3 sm:grid-cols-2 max-w-xl"
          onSubmit={(e) => void create(e)}
        >
          <PartFormFields
            idPrefix="new"
            form={form}
            onFormChange={setForm}
            locations={locations}
            customFields={customFields}
            customFieldValues={customFieldValues}
            onCustomFieldChange={(fieldKey, value) =>
              setCustomFieldValues((prev) => ({ ...prev, [fieldKey]: value }))
            }
          />
          {formError && <p className="text-sm text-destructive sm:col-span-2">{formError}</p>}
          <Button type="submit" disabled={saving} className="w-fit bg-[#1e3a4f] hover:bg-[#163041]">
            {saving ? "Creating…" : "Create part"}
          </Button>
        </form>
      )}

      {editingId && (
        <form
          className="bg-white rounded-lg shadow p-4 grid gap-3 sm:grid-cols-2 max-w-xl border-2 border-[#1e3a4f]/20"
          onSubmit={(e) => void saveEdit(e)}
        >
          <p className="sm:col-span-2 text-sm font-semibold text-[#1e3a4f]">Edit part</p>
          <PartFormFields
            idPrefix="edit"
            form={form}
            onFormChange={setForm}
            locations={locations}
            customFields={customFields}
            customFieldValues={customFieldValues}
            onCustomFieldChange={(fieldKey, value) =>
              setCustomFieldValues((prev) => ({ ...prev, [fieldKey]: value }))
            }
          />
          {formError && <p className="text-sm text-destructive sm:col-span-2">{formError}</p>}
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" disabled={saving} className="bg-[#1e3a4f] hover:bg-[#163041]">
              {saving ? "Saving…" : "Save changes"}
            </Button>
            <Button type="button" variant="secondary" disabled={saving} onClick={cancelEdit}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <DataTable
        columns={[
          { key: "name", header: "Part name", render: (p) => p.part_name },
          { key: "num", header: "Part #", render: (p) => p.part_number || "—" },
          { key: "loc", header: "Location", render: (p) => p.location_name || "—" },
          { key: "on", header: "On-hand", className: "text-right", render: (p) => String(p.on_hand) },
          {
            key: "actions",
            header: "",
            render: (p) => (
              <Button
                size="sm"
                variant="secondary"
                disabled={editingId === p.id}
                onClick={() => startEdit(p)}
              >
                Edit
              </Button>
            ),
          },
        ]}
        rows={parts}
        rowKey={(p) => p.id}
      />
    </div>
  );
}
